import { Component } from '@theme/component';
import {
  fetchConfig,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {Map<number, number>} */
  #quantityChangeTimers = new Map();
  /** @type {Promise<void>} */
  #updateQueue = Promise.resolve();
  /** @type {AbortController | null} */
  #activeRequest = null;
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /** @param {Event} event */
  #handleQuantityChange = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const line = event.detail.cartLine;
    if (!line) return;
    const quantity = event.detail.quantity;

    const existingTimer = this.#quantityChangeTimers.get(line);
    if (existingTimer) clearTimeout(existingTimer);

    const timer = setTimeout(() => {
      this.#quantityChangeTimers.delete(line);
      this.#onQuantityChange(quantity, line);
    }, 100);
    this.#quantityChangeTimers.set(line, timer);
  };

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    for (const timer of this.#quantityChangeTimers.values()) clearTimeout(timer);
    this.#quantityChangeTimers.clear();
    this.#activeRequest?.abort();
  }

  /**
   * Handles a debounced cart quantity change.
   * @param {number} quantity - The requested quantity.
   * @param {number} line - The cart line number.
   */
  #onQuantityChange(quantity, line) {
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    if (quantity === 0) return this.onLineItemRemove(line);

    this.#enqueueUpdate({
      line,
      lineId: lineItemRow.dataset.key ?? '',
      quantity,
      action: 'change',
    });

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
  * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const timer = this.#quantityChangeTimers.get(line);
    if (timer) {
      clearTimeout(timer);
      this.#quantityChangeTimers.delete(line);
    }

    cartItemRowToRemove.classList.add('is-cart-pending');
    cartItemRowToRemove.setAttribute('aria-busy', 'true');
    return this.#enqueueUpdate({
      line,
      lineId: cartItemRowToRemove.dataset.key ?? '',
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Runs cart changes in order so bundled section responses cannot overwrite a newer cart state.
   * @param {{line: number, lineId: string, quantity: number, action: string}} config
   */
  #enqueueUpdate(config) {
    const queuedUpdate = this.#updateQueue.catch(() => undefined).then(() => this.updateQuantity(config));
    this.#updateQueue = queuedUpdate.then(() => undefined).catch(() => undefined);
    return queuedUpdate;
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} config.lineId - The stable line-item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, lineId, quantity } = config;
    const { cartTotal } = this.refs;
    const lineItemRow = this.#findCartItemRow(line, lineId);
    lineItemRow?.classList.add('is-cart-pending');
    lineItemRow?.setAttribute('aria-busy', 'true');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const requestBody = {
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };
    if (lineId) requestBody.id = lineId;
    else requestBody.line = line;
    const body = JSON.stringify(requestBody);

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    const requestController = new AbortController();
    let requestTimedOut = false;
    const requestTimeout = setTimeout(() => {
      requestTimedOut = true;
      requestController.abort();
    }, 8000);
    this.#activeRequest = requestController;

    return fetch(`${Theme.routes.cart_change_url}`, {
      ...fetchConfig('json', { body }),
      signal: requestController.signal,
    })
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, lineId, parsedResponseText.errors);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const sectionHTML = parsedResponseText.sections?.[this.sectionId];
        if (!sectionHTML) throw new Error('Cart response did not include the requested section.');

        const newSectionHTML = new DOMParser().parseFromString(
          sectionHTML,
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        await morphSection(this.sectionId, sectionHTML, {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
        this.#announce('Cart updated.');
      })
      .catch((error) => {
        if (error?.name !== 'AbortError' || requestTimedOut) console.error(error);
        deferredUpdatePromise.reject(error);
        if (!this.isConnected) return;
        const message = requestTimedOut
          ? 'Cart update timed out. Please try again.'
          : error?.message || 'Failed to update cart';
        this.#handleCartError(line, lineId, message, 'SERVICE_UNAVAILABLE');
      })
      .finally(() => {
        clearTimeout(requestTimeout);
        if (this.#activeRequest === requestController) this.#activeRequest = null;
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {string} lineId - The stable line-item key.
   * @param {string} message - The error message.
   * @param {string} [code] - The standardized error code.
   */
  #handleCartError = (line, lineId, message, code = 'INVALID') => {
    const row = this.#findCartItemRow(line, lineId);
    const quantityInput = row?.querySelector('input');
    if (quantityInput instanceof HTMLInputElement) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = row?.querySelector('.cart-item__error-text');
    const cartItemErrorContainer = row?.querySelector('.cart-items__error');
    if (cartItemError instanceof HTMLElement) cartItemError.textContent = message;
    if (cartItemErrorContainer instanceof HTMLElement) cartItemErrorContainer.classList.remove('hidden');

    row?.classList.remove('is-cart-pending');
    row?.removeAttribute('aria-busy');
    this.#announce(message);

    this.dispatchEvent(
      new CartErrorEvent({
        error: message || 'Cart update failed',
        code,
      })
    );
  };

  /** @param {number} line @param {string} lineId */
  #findCartItemRow(line, lineId) {
    if (lineId) {
      const keyedRow = this.refs.cartItemRows?.find((row) => row.dataset.key === lineId);
      if (keyedRow) return keyedRow;
    }
    return this.refs.cartItemRows?.[line - 1];
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.setAttribute('aria-busy', 'true');
    this.#announce('Updating cart.');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');
    for (const row of this.refs.cartItemRows ?? []) {
      row.classList.remove('is-cart-pending');
      row.removeAttribute('aria-busy');
    }
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.querySelector('[data-cart-status]');
    if (status instanceof HTMLElement) status.textContent = message;
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
